import { useState, useEffect, useRef } from "react";
import { FaGear, FaLightbulb, FaCalendar } from "react-icons/fa6";
import { useGame } from "../../../context/GameContext";
import { FaUndo } from "react-icons/fa";
import { undoLastMove } from "../../../utils/gameControls";
import { toggleInGameMenu } from "../../../utils/navigationHelpers";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { KeyCodes } from "../../../utils/navigationHelpers";
import useSoundEffect from "../../../hooks/useSoundEffect";
import ChessButton from "../../visual/ChessButton/ChessButton";
import { useMultiplayer } from "../../../context/MultiplayerContext";
import { Chess } from "chess.js";
import PuzzleCalendar from "../PuzzleCalendar/PuzzleCalendar";
/**
 * The menu icon in the top right of the screen
 */
export default function MenuIcon() {
    const [showUndoTooltip, setShowUndoTooltip] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const exitReviewButtonRef = useRef<HTMLButtonElement>(null);

    const { gameState, setGameState, setRemoteNavigation, remoteNavigation } = useGame();
    const { multiplayerState } = useMultiplayer();
    const showUndoButton = gameState.isAIGame;
    const { playClickAudio, playSelectAudio } = useSoundEffect();

    // Puzzle hint logic
    const showHintButton = gameState.isPuzzle && !gameState.game?.isGameOver();
    const currentIndex = gameState.puzzleIndex ?? 0;
    const solution = gameState.puzzleSolution ?? [];
    const puzzleSolved = currentIndex >= solution.length;

    const handleHint = () => {
        if (puzzleSolved || !gameState.game) {
            console.log("Hint blocked - puzzle solved or no game");
            return;
        }

        const expectedMoveSAN = solution[currentIndex];
        console.log("Expected move SAN:", expectedMoveSAN);

        // Parse the move to find the source and destination squares
        const tempGame = new Chess(gameState.game.fen());
        const allMoves = tempGame.moves({ verbose: true });
        const correctMove = allMoves.find((move) => move.san === expectedMoveSAN);

        console.log("Correct move found:", correctMove);

        if (correctMove) {
            const currentHintLevel = gameState.puzzleHintLevel || 0;

            if (currentHintLevel === 0) {
                // First hint: Show source square only
                console.log("Setting hint square to:", correctMove.from);
                setGameState((prev) => ({
                    ...prev,
                    puzzleHintSquare: correctMove.from,
                    puzzleHintDestination: null,
                    puzzleHintLevel: 1,
                }));

                // Clear hint after 3 seconds
                setTimeout(() => {
                    console.log("Clearing hint square");
                    setGameState((prev) => ({
                        ...prev,
                        puzzleHintSquare: null,
                        puzzleHintDestination: null,
                        puzzleHintLevel: 0,
                    }));
                }, 3000);
            } else if (currentHintLevel === 1) {
                // Second hint: Show destination square with arrow
                console.log("Setting hint destination to:", correctMove.to);
                setGameState((prev) => ({
                    ...prev,
                    puzzleHintSquare: correctMove.from,
                    puzzleHintDestination: correctMove.to,
                    puzzleHintLevel: 2,
                }));

                // Clear hint after 3 seconds
                setTimeout(() => {
                    console.log("Clearing hint square and destination");
                    setGameState((prev) => ({
                        ...prev,
                        puzzleHintSquare: null,
                        puzzleHintDestination: null,
                        puzzleHintLevel: 0,
                    }));
                }, 3000);
            }
        } else {
            console.error("Could not find correct move for SAN:", expectedMoveSAN);
        }
        playSelectAudio();
    };

    const handleExitReview = () => {
        setGameState((prev) => ({ ...prev, isReviewMode: false }));
        setRemoteNavigation((prev) => ({
            ...prev,
            isInMenuIcons: false,
            selectedMenuIcon: null,
            isInGameOverMenu: true,
            selectedGameOverIcon: null,
        }));
        playSelectAudio();
    };

    // Only show review button when game is over (including forfeit) and in review mode
    const showReviewButton =
        (gameState.game?.isGameOver() || multiplayerState.forfeit.isForfeit) &&
        gameState.isReviewMode;

    // Focus on Exit Review button when review mode is activated
    useEffect(() => {
        if (showReviewButton) {
            // Add a small delay to prevent immediate triggering from the same Enter keypress
            setTimeout(() => {
                exitReviewButtonRef.current?.focus();
            }, 100);
        }
    }, [showReviewButton]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                KeyCodes.Ups.includes(event.key) ||
                KeyCodes.Downs.includes(event.key) ||
                KeyCodes.Rights.includes(event.key)
            ) {
                playClickAudio();
            }
        };
        if (remoteNavigation.isInMenuIcons) {
            window.addEventListener("keydown", handleKeyDown);
        }
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [remoteNavigation.isInMenuIcons, playClickAudio]);

    const isGearHovered =
        remoteNavigation.isInMenuIcons && remoteNavigation.selectedMenuIcon === "Options";

    const isUndoHovered =
        remoteNavigation.isInMenuIcons && remoteNavigation.selectedMenuIcon === "Undo";

    const showTooltip = () => {
        setShowUndoTooltip(true);

        // Clear any existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Set new timeout
        timeoutRef.current = setTimeout(() => {
            setShowUndoTooltip(false);
            timeoutRef.current = null;
        }, 2000);
    };

    const hideTooltip = () => {
        setShowUndoTooltip(false);
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    // Handle keyboard navigation
    useEffect(() => {
        if (isUndoHovered) {
            showTooltip();
        } else {
            hideTooltip();
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [isUndoHovered, playClickAudio]);
    // Always show gear and calendar for puzzles
    const showGearIcon = true;
    const showCalendarIcon = gameState.isPuzzle;

    return (
        <>
            {showCalendar && <PuzzleCalendar onClose={() => setShowCalendar(false)} />}
            <div id="menu-icons" className={showReviewButton ? "menu-icons--with-review" : ""}>
                {showReviewButton && (
                    <div className="menu-icons__review-column">
                        <ChessButton
                            ref={exitReviewButtonRef}
                            className="exit-review-button"
                            onClick={handleExitReview}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleExitReview();
                                }
                            }}
                            aria-label="Exit Review Mode"
                            data-navrow={0}
                            data-navcol={0}
                        >
                            Exit Review
                        </ChessButton>
                    </div>
                )}
                <div className="menu-icons__controls-column">
                    {/* Gear icon - always show */}
                    {showGearIcon && (
                        <FaGear
                            onClick={() => {
                                playSelectAudio();
                                toggleInGameMenu(setGameState);
                            }}
                            className={`menu-icon ${isGearHovered && "remote-hovered-icon"}`}
                            data-testid="open-menu"
                        />
                    )}

                    {/* Undo button - only for AI games */}
                    {showUndoButton && (
                        <>
                            <FaUndo
                                data-tooltip-id="undo-tooltip"
                                data-tooltip-content="Undo last move"
                                onClick={() => {
                                    playSelectAudio();
                                    undoLastMove(setGameState);
                                }}
                                onMouseEnter={showTooltip}
                                onMouseLeave={hideTooltip}
                                className={`menu-icon ${isUndoHovered && "remote-hovered-icon"}`}
                            />
                            <ReactTooltip
                                id="undo-tooltip"
                                className="undo-tooltip"
                                place="left"
                                isOpen={showUndoTooltip}
                                openOnClick={false}
                                border="2px solid var(--border-color)"
                                style={{
                                    fontSize: "40px",
                                    padding: "12px 16px",
                                    maxWidth: "300px",
                                    borderRadius: "8px",
                                    backgroundColor: "#321607",
                                    color: "#fae1c3",
                                    fontFamily: "Georgia, Times New Roman, serif",
                                    display: window.innerWidth < 768 ? "none" : "block",
                                }}
                            />
                        </>
                    )}

                    {/* Hint button - only when puzzle is active and not solved */}
                    {showHintButton && !puzzleSolved && (
                        <FaLightbulb
                            onClick={handleHint}
                            className="menu-icon hint-icon"
                            title="Get a hint"
                            style={{ marginTop: "0.5rem" }}
                        />
                    )}

                    {/* Calendar button - always show for puzzles */}
                    {showCalendarIcon && (
                        <FaCalendar
                            onClick={() => {
                                playSelectAudio();
                                setShowCalendar(true);
                            }}
                            className="menu-icon calendar-icon"
                            title="Puzzle Calendar"
                            style={{ marginTop: "0.5rem" }}
                        />
                    )}
                </div>
            </div>
        </>
    );
}
